import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, signal, input } from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {select, Store} from '@ngrx/store';
import {
  getInfluencerError,
  getInfluencersReferralsListPaginated,
  IInfluencersState,
  InfluencersReferralsListPaginated,
  ResetInfluencerState
} from '@app/stores';
import {ToastrService} from 'ngx-toastr';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {Influencer} from '@app/modules/shared/models';
import { SbTableComponent } from '@app/_components/unified/sb-table/sb-table.component';
import { SbTableCellDirective } from '@app/_components/unified/sb-table/sb-table.directives';
import { SbTableColumn, SbTableOptions, SbTableServerState } from '@app/_components/unified/sb-table/sb-table.types';

@Component({
  selector: 'app-referrals-list',
  templateUrl: './referrals-list.component.html',
  styleUrls: ['./referrals-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SbTableComponent, SbTableCellDirective]
})
export class ReferralsListComponent implements OnInit, OnDestroy {
  modal = inject(NgbActiveModal);
  private influencerStore = inject<Store<IInfluencersState>>(Store);
  private toastr = inject(ToastrService);

  // The list does not sort: the ids only name the cells. The columns after Company show with
  // "Display all columns" (hidden by the stylesheet, so the table is not rebuilt).
  readonly tableColumns: SbTableColumn[] = [
    {id: 'name', header: 'Name', field: 'name'},
    {id: 'email', header: 'Email', field: 'email'},
    {id: 'influencer_type', header: 'Influencer type'},
    {id: 'segment', header: 'Segment'},
    {id: 'registration_url', header: 'Registration Url', field: 'registration_url'},
    {id: 'company', header: 'Company', field: 'company'},
    ...[
      {id: 'first_name', header: 'First name', field: 'first_name'},
      {id: 'last_name', header: 'Last name', field: 'last_name'},
      {id: 'phone', header: 'Phone', field: 'phone'},
      {id: 'video_url', header: 'Video Url', field: 'video_url'},
      {id: 'booth_number', header: 'Booth Number', field: 'booth_number'},
      {id: 'job_title', header: 'Job Title', field: 'job_title'},
      {id: 'sessions', header: 'Sessions'},
      ...Array.from({length: 10}, (_, i) => ({id: `custom_${i + 1}`, header: `Custom ${i + 1}`, field: `custom_${i + 1}`})),
    ].map((column) => ({...column, className: 'referrals-extra-column', headerClass: 'referrals-extra-column'})),
  ];
  readonly tableOptions: SbTableOptions = {
    serverSide: true,
    pageLength: 10,
    lengthMenu: [10, 25, 50, 100],
    ordering: false,
    selectable: false,
    searching: true,
    tableId: 'referral-table',
  };
  readonly totalRows = signal(0);
  readonly influencerId = input<number>(undefined);
  readonly influencerName = input<string>(undefined);
  readonly influencerTypes = input([]);
  readonly segmentsGlobal = input([]);
  private unsubscriber = new Subject();
  readonly influencersList = signal<Influencer[]>([]);
  showAll: boolean;

  subscribeStores() {
    this.influencerStore.pipe(select(getInfluencerError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });

    this.influencerStore.pipe(select(getInfluencersReferralsListPaginated))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(paginatedReferralsList => {
        if (paginatedReferralsList) {
          this.influencersList.set(paginatedReferralsList.list);
          this.totalRows.set(paginatedReferralsList.paging.total);
        }
      });
  }

  ngOnInit(): void {
    this.influencerStore.dispatch(ResetInfluencerState({params: {error: '', paginatedReferralsList: null}}));
    this.subscribeStores();
  }

  getSessionTitle(influencer) {
    return influencer?.sessions?.map(item => item.title).join(' | ') || '';
  }

  /** The table asks for a page (it sends no sort: the list does not sort). */
  onTableState(state: SbTableServerState) {
    this.influencerStore.dispatch(InfluencersReferralsListPaginated({influencerId: this.influencerId(), params: {
      options: JSON.stringify({includePagination: true}),
      page: state.page,
      perPage: state.pageLength,
      search: state.search
    }}));
  }

  showAllColumns(checked: boolean) {
    this.showAll = checked;
    // The table lines its header up with the body again on a resize.
    setTimeout(() => window.dispatchEvent(new Event('resize')), 200);
  }

  getInfluencerType(influencerTypeId) {
    const type = this.influencerTypes().find(item => item.id === influencerTypeId);
    return type ? type.name : '';
  }

  getInfluencerSegment(segmentId) {
    const type = this.segmentsGlobal().find(item => item.id === segmentId);
    return type ? type.name : '';
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }
}
